import { Injectable, inject } from '@angular/core';
import type { EchartsConfig, ILaneConfig, LaneConfig, ZoomSliderMode } from 'commons';
import type { TitleOption } from 'echarts/types/dist/shared';
import { CHART_CONSTANTS } from './echarts-config.types';
import { GridBuilderService } from './grid-builder.service';

/**
 * Service responsible for building title configurations for ECharts multilane charts.
 */
@Injectable({ providedIn: 'root' })
export class TitleBuilderService {
  private readonly gridBuilder = inject(GridBuilderService);

  /**
   * Builds title configurations for all lanes.
   *
   * Title positioning logic:
   * - First title (i=0): Always positioned above the first grid
   * - In LANE mode (subsequent titles): Positioned between previous grid's zoom slider and current grid
   * - In non-LANE modes: All titles positioned above their respective grids
   */
  buildTitles(
    lanes: LaneConfig[] | ILaneConfig[],
    zoomSliderMode: ZoomSliderMode,
    gridHeight: number = CHART_CONSTANTS.DEFAULT_GRID_HEIGHT,
    config?: EchartsConfig
  ): TitleOption[] {
    const titleOffset = config?.titleOffset ?? 20;
    const titleFontSize = config?.titleFontSize ?? 12;
    const titleLeft = config?.titleLeft;

    return lanes.map((lane, index) => this.createTitle({ lane, index }, { zoomSliderMode, gridHeight }, { titleOffset, titleFontSize, titleLeft, config }));
  }

  private createTitle(
    laneContext: { lane: LaneConfig | ILaneConfig; index: number },
    layout: { zoomSliderMode: ZoomSliderMode; gridHeight: number },
    titleConfig: {
      titleOffset: number;
      titleFontSize: number;
      titleLeft: number | undefined;
      config?: EchartsConfig;
    }
  ): TitleOption {
    const { lane, index } = laneContext;
    const { zoomSliderMode } = layout;
    const { titleOffset, titleFontSize, titleLeft, config } = titleConfig;

    const top = this.calculateTitleTop(index, zoomSliderMode, titleOffset, config);

    const titleStyle = this.createTitleTextStyle(config, titleFontSize);

    return {
      text: lane.config.label ?? `Lane${index + 1}`,
      show: config?.titleShow ?? true,
      top,
      left: titleLeft === undefined ? (config?.titleAlign ?? 'left') : `${titleLeft}%`,
      textStyle: titleStyle,
      subtextStyle: {
        fontSize: config?.subtitleFontSize ?? 10,
        color: config?.subtitleColor ?? '#666',
        fontWeight: config?.subtitleFontWeight ?? 'normal',
      },
      itemGap: config?.titleItemGap ?? 5,
      padding: config?.titlePadding ?? 0,
      backgroundColor: config?.titleBackgroundColor ?? 'transparent',
      borderColor: config?.titleBorderColor ?? 'transparent',
      borderWidth: config?.titleBorderWidth ?? 0,
      borderRadius: config?.titleBorderRadius ?? 0,
    };
  }

  private createTitleTextStyle(config?: EchartsConfig, defaultFontSize?: number) {
    return {
      fontSize: config?.titleFontSize ?? defaultFontSize ?? 12,
      fontWeight: config?.titleFontWeight ?? 'bold',
      fontFamily: config?.titleFontFamily ?? 'sans-serif',
      color: config?.titleColor ?? '#333',
      lineHeight: config?.titleLineHeight ?? 14,
      textShadowColor: config?.titleTextShadowColor ?? 'transparent',
      textShadowBlur: config?.titleTextShadowBlur ?? 0,
      textShadowOffsetX: config?.titleTextShadowOffsetX ?? 0,
      textShadowOffsetY: config?.titleTextShadowOffsetY ?? 0,
    };
  }

  private calculateTitleTop(index: number, zoomSliderMode: ZoomSliderMode, titleOffset: number, config?: EchartsConfig): number {
    // First title: position above the first grid
    // if (index === 0) {
    return this.gridBuilder.calculateGridTopPosition(index, zoomSliderMode, config) - titleOffset;
  }
}
